import { useState, useEffect } from 'react';

import { PieChart } from '@mui/x-charts';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import BasicPopup from '../../Popups/BasicPopup/BasicPopup';
import CustomDateForm from '../../../Forms/Common/CustomDateForm/CustomDateForm';
import styles from '../../../../pages/Profile/ProfileInsights/ProfileInsights.module.css';
import {
    AusMapIcon,
    BlueMapIcon,
    YellowMapIcon
} from '../../../../assets/images/icons';


export const EmployerStats = () => {
    const [activeView, setActiveView] = useState('yearly');
    const [radius, setRadius] = useState(70);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const [cx, setCx] = useState(100);
    const [cy, setCy] = useState(100);
    const [topCountries, settopCountries] = useState([]);
    const [isOpenCustomDatePopup, setisOpenCustomDatePopup] = useState(false);
    const [topIndustries, settopIndustries] = useState([]);
    const { t } = useTranslation();

    const handleViewChange = (view) => {
        setActiveView(view);
        if (view == 'custom') { handlePopupOpen() }
    };

    // Update cx and cy based on window size
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            if (width < 860) {
                setCx(width / 4);
                setCy(100);
            } else {
                setCx(100);
                setCy(100);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const profileInsights = useSelector(state => state.user.user.profileInsights);

    const filterInsights = () => {
        const now = new Date();
        const filteredInsights = profileInsights.filter((insight) => {
            const viewedAt = new Date(insight.viewed_at); // Parse `viewed_at` into a Date object
            if (activeView === 'custom' && formik.values.custom.length > 0) {
                const startDate = new Date(formik.values.custom[0].startDate);
                const endDate = new Date(formik.values.custom[0].endDate);
                return viewedAt >= startDate && viewedAt <= endDate;
            } else if (activeView === 'daily') {
                return viewedAt.toDateString() === now.toDateString(); // Same day check
            } else if (activeView === 'weekly') {
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(now.getDate() - 7);
                return viewedAt >= sevenDaysAgo && viewedAt <= now; // Within the last 7 days
            } else if (activeView === 'monthly') {
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                return viewedAt >= startOfMonth && viewedAt <= now; // Current month
            } else if (activeView === 'yearly') {
                const startOfYear = new Date(now.getFullYear(), 0, 1);
                return viewedAt >= startOfYear && viewedAt <= now; // Current year
            }
            return false;
        });
        return filteredInsights;
    };

    const formik = useFormik({
        initialValues: {
            custom: []
        },
        onSubmit: values => { }
    });

    const handlePopupClose = () => setisOpenCustomDatePopup(false);

    const handlePopupOpen = (popupName) => setisOpenCustomDatePopup(true);

    const handleAddEducation = (values) => {
        formik.setFieldValue('custom', [{ id: Date.now(), ...values }]);
        handlePopupClose();
    }

    useEffect(() => {
        const filterData = filterInsights();
        const getTopThree = (array, key) => {
            const countMap = array.reduce((acc, item) => {
                const value = item[key];
                if (value) {
                    acc[value] = (acc[value] || 0) + 1;
                }
                return acc;
            }, {});

            return Object.entries(countMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3);
        };
        const topCountries = getTopThree(filterData, 'country');
        settopCountries(topCountries)
        const topIndustries = getTopThree(filterData, 'company_industries');
        settopIndustries(topIndustries)
    }, [profileInsights, activeView, formik.values.custom]);

    return (
        <div className={styles.sectionWrapper} style={{ margin: '20px 0px' }}>
            {isOpenCustomDatePopup &&
                <BasicPopup
                    isOpen={isOpenCustomDatePopup}
                    closePopup={() => handlePopupClose()}
                >
                    <CustomDateForm onSubmit={handleAddEducation} />
                </BasicPopup>
            }
            <div className={styles.header} style={{ paddingBottom: '30px' }}>
                <p className={styles.heading}>{t('employerStats.heading')}</p>
                <div className={styles.buttonGroup}>
                    {["custom", 'Daily', 'Weekly', 'Monthly', 'Yearly'].map((label, index) => (
                        <>
                            <button
                                key={label}
                                className={
                                    activeView === label.toLowerCase()
                                        ? `${styles.button} ${styles.active}`
                                        : styles.button
                                }
                                onClick={() => handleViewChange(label.toLowerCase())}
                            >
                                {t(`profileViews.buttons.${label.toLowerCase()}`)} {/* Translated buttons */}
                            </button>
                            {index < 3 && <div className={styles.saperator}></div>}
                        </>
                    ))}
                </div>
            </div>
            <div className={styles.employerStatsBody}>
                <div className={styles.countryListContainer}>
                    <p className={styles.headingTitle}>{t('employerStats.location')}</p>
                    <div className={styles.countryListSubContainer}>
                        {topCountries.length > 0 ? (
                            topCountries.map((country, index) => {
                                const icons = [AusMapIcon, YellowMapIcon, BlueMapIcon];
                                const iconToShow = icons[index % icons.length];
                                return (
                                    <div className={styles.countryList} key={index.toString()}>
                                        <img className={styles.mapIcon} src={iconToShow} alt="Map Icon Logo" />
                                        <div>
                                            <p className={styles.countryTitle}>{t('general.country.' + country[0])}</p>
                                            <p className={styles.countryViews}>{`${country[1]} views`}</p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className={styles.noDataMsg}>{t('employerStats.noData')}</p>
                        )}
                    </div>
                </div>
                <div className={styles.paiContainer}>
                    <p className={styles.headingTitle} style={{ paddingLeft: "0px" }}>{t('employerStats.topIndustries')}</p>
                    <div className={styles.chartSubContainer}>
                        <PieChart
                            colors={topIndustries.length > 0 ? ['#09AEEF', '#0464B2', '#B2D7F5'] : ['#f300ff']} // Baby pink if no data
                            series={[
                                {
                                    data: topIndustries.length > 0 ?
                                        topIndustries.map(industry => ({ label: industry[0], value: industry[1] })) :
                                        [{ label: 'No Data', value: 1 }], // Show 'No Data' with value 1 if empty
                                    innerRadius: radius,
                                    arcLabelMinAngle: 20,
                                    cx: cx,
                                    cy: cy,
                                },
                            ]}
                            slotProps={{ legend: { hidden: true } }}
                            skipAnimation={skipAnimation}
                        />
                    </div>
                    <div className={styles.radioContainer} style={{ flexWrap: "wrap", }}>
                        {topIndustries.map((industry, index) => (
                            <div className={styles.radioLabel} key={industry[0]} style={{ display: 'flex', alignItems: 'center' }}>
                                <span
                                    style={{
                                        height: '15px',
                                        width: '15px',
                                        border: `2px solid ${['#09AEEF', '#0464B2', '#B2D7F5'][index % 3]}`,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        marginRight: '10px',
                                    }}
                                />
                                {industry[0]}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
